import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArticleCard } from "./ArticleCard";
import { ContactBanner } from "./ContactBanner";
import { roleToCategory } from "src/utils/helpCenterRoleToCategory";
import { HelpCenterArticleSearchBar } from "./searchBar.jsx";

const ArrowLeftIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

const Breadcrumb = ({ searchTerm }) => {
  const navigate = useNavigate();

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate("/help");
  };
  return (
    <div className="flex items-center gap-1.5 text-sm text-gray-500 px-8 pt-5">
      <ArrowLeftIcon />
      <button
        href="/help"
        className="hover:underline"
        onClick={handleBackClick}
      >
        Back to Help Center
      </button>
      {searchTerm && (
        <>
          <span className="text-gray-400">/</span>
          <span className="text-orange-500 font-medium">{searchTerm}</span>
        </>
      )}
    </div>
  );
};

export default function RolesPage() {
  //get the role from the url, if not present, default to planner
  const [searchParams] = useSearchParams();
  const allArticles = useSelector((state) => state.helpCenterArticles);
  //filtered articles based on role and category
  const [filteredArticles, setFilteredArticles] = useState([]);
  let role = searchParams.get("role") || "Planner";
  if (!["Event Host", "Planner", "Performer", "Crew"].includes(role)) {
    role = "Planner";
  }
  const categories = roleToCategory(role);
  const urlCategory = searchParams.get("category");
  const [activeCategory, setActiveCategory] = useState(
    (categories.includes(urlCategory) && urlCategory) || categories[0],
  );

  //filter articles based on role and category
  useEffect(() => {
    const filtered = allArticles.filter((article) => {
      return article.role === role && article.category === activeCategory;
    });
    setFilteredArticles(filtered);
  }, [role, activeCategory, allArticles]);

  return (
    <div className="min-h-screen bg-white font-sans mt-20 px-4 md:px-32">
      {/* Breadcrumb */}
      <Breadcrumb searchTerm={role} />

      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-6 md:px-10 pt-5 pb-0">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Articles for <span className="text-[#E8441A]">{role}</span>
        </h1>
        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 w-full md:w-64 relative">
          <HelpCenterArticleSearchBar />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col md:flex-row md:gap-16 px-6 md:px-10 py-6 gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-72 flex-shrink-0 self-start h-fit border border-gray-150 rounded-xl p-5 bg-white">
          <p className="text-2xl font-semibold text-gray-800 mb-3">
            Categories
          </p>
          <div>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`w-full text-left px-4 py-2.5 mb-1 text-sm transition-colors rounded-lg ${
                  activeCategory === cat
                    ? "bg-[#FAECE7] text-[#E8441A] font-semibold pl-6 "
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Articles */}
        <div className="flex-1 flex flex-col gap-6">
          {filteredArticles.map((article) => (
            <ArticleCard key={article._id} article={article} len={200} />
          ))}
        </div>
      </div>

      <ContactBanner />
    </div>
  );
}
